import { WeibullUser } from '@/contexts/CognitoAuthContext';

export type UserRole = 'admin' | 'user';
export type UserCompany = 'onyx' | 'company';

export interface Permission {
  role: UserRole | 'any';
  company: UserCompany | 'any';
}

// Define all permissions based on the backend matrix:

// | Category       | Endpoint            | Role  | Company |
// | -------------- | ------------------- | ----- | ------- |
// | Authentication | id                  | admin | onyx    |
// |                | all                 | admin | onyx    |
// |                | find                | admin | onyx    |
// |                | get_me              | user  | any     |
// |                | register            | admin | onyx    |
// |                | update              | admin | onyx    |
// | Failures       | failures            | user  | any     |
// |                | failure_id          | user  | any     |
// |                | analysis filter     | user  | any     |
// |                | failure_filter      | user  | any     |
// |                | new                 | user  | any     |
// |                | update              | admin | any     |
// |                | filter_params       | user  | any     |
// | sites          | sites               | user  | any     |
// |                | site_id             | user  | any     |
// |                | site_filter         | user  | any     |
// |                | new                 | admin | onxy    |
// |                | filter_string       | user  | any     |
// |                | update              | admin | onyx    |
// | turbines       | turbines            | user  | any     |
// |                | turbine_id          | user  | any     |
// |                | turbine_filter      | user  | any     |
// |                | turbine_filter_list | user  | any     |
// |                | new                 | admin | onyx    |
// |                | update              | admin | onyx    |

export const PERMISSIONS = {
  // Auth endpoints
  auth: {
    id: { role: 'admin' as UserRole, company: 'onyx' as UserCompany } as Permission,
    all: { role: 'admin' as UserRole, company: 'onyx' as UserCompany } as Permission,
    find: { role: 'admin' as UserRole, company: 'onyx' as UserCompany } as Permission,
    getMe: { role: 'any', company: 'any' } as Permission,
    register: { role: 'admin' as UserRole, company: 'onyx' as UserCompany } as Permission,
    update: { role: 'admin' as UserRole, company: 'onyx' as UserCompany } as Permission,
  },

  // Failures endpoints
  failures: {
    list: { role: 'any', company: 'any' } as Permission,
    view: { role: 'any', company: 'any' } as Permission,
    analysisFilter: { role: 'any', company: 'any' } as Permission,
    failureFilter: { role: 'any', company: 'any' } as Permission,
    create: { role: 'any', company: 'any' } as Permission,
    update: { role: 'admin' as UserRole, company: 'any' } as Permission,
  },

  // Filter params
  filterParams: {
    list: { role: 'any', company: 'any' } as Permission,
  },

  // Sites endpoints
  sites: {
    list: { role: 'any', company: 'any' } as Permission,
    view: { role: 'any', company: 'any' } as Permission,
    filter: { role: 'any', company: 'any' } as Permission,
    create: { role: 'admin' as UserRole, company: 'onyx' as UserCompany } as Permission, // Changed: now requires admin + onyx
    filterString: { role: 'any', company: 'any' } as Permission, // Changed: now any user
    update: { role: 'admin' as UserRole, company: 'onyx' as UserCompany } as Permission,
  },

  // Turbines endpoints
  turbines: {
    list: { role: 'any', company: 'any' } as Permission,
    view: { role: 'any', company: 'any' } as Permission,
    filter: { role: 'any', company: 'any' } as Permission,
    filterList: { role: 'any', company: 'any' } as Permission,
    create: { role: 'admin' as UserRole, company: 'onyx' as UserCompany } as Permission,
    update: { role: 'admin' as UserRole, company: 'onyx' as UserCompany } as Permission,
  },

  // Workspaces endpoints
  workspaces: {
    list: { role: 'any', company: 'any' } as Permission,
    view: { role: 'any', company: 'any' } as Permission,
    create: { role: 'any', company: 'any' } as Permission,
    // Note: update and delete have additional ownership checks in workspacePermissions.ts
    update: { role: 'any', company: 'any' } as Permission,
    delete: { role: 'admin', company: 'any' } as Permission,
  },
} as const;

// Helper function to check if user has permission
export function hasPermission(user: WeibullUser | null, permission: Permission): boolean {
  if (!user) return false;

  // Extract role from user
  const userRole: UserRole = user.role || 'user'; // Use the role from user object
  const userCompany: UserCompany = user.company; // Now properly typed as 'onyx' | 'company'

  // Check role
  if (permission.role !== 'any' && permission.role !== userRole) {
    return false;
  }

  // Check company
  if (permission.company !== 'any' && permission.company !== userCompany) {
    return false;
  }

  return true;
}

// Convenience functions for common permission checks
export const canCreateSite = (user: WeibullUser | null): boolean =>
  hasPermission(user, PERMISSIONS.sites.create);

export const canUpdateSite = (user: WeibullUser | null): boolean =>
  hasPermission(user, PERMISSIONS.sites.update);

export const canCreateTurbine = (user: WeibullUser | null): boolean =>
  hasPermission(user, PERMISSIONS.turbines.create);

export const canUpdateTurbine = (user: WeibullUser | null): boolean =>
  hasPermission(user, PERMISSIONS.turbines.update);

export const canUpdateFailure = (user: WeibullUser | null): boolean =>
  hasPermission(user, PERMISSIONS.failures.update);

export const canAccessUserManagement = (user: WeibullUser | null): boolean =>
  hasPermission(user, PERMISSIONS.auth.all);

// Check if user is an ONYX admin
export const isOnyxAdmin = (user: WeibullUser | null): boolean => {
  if (!user) return false;
  return user.company === 'onyx' && user.role === 'admin';
};

// Check if user is any admin
export const isAdmin = (user: WeibullUser | null): boolean => {
  if (!user) return false;
  return user.role === 'admin';
};
